"use client";

/**
 * Interactive hero pieces for the landing page (2026-07 redesign):
 *  - RotatingExamWord: cycles exam names inside the hero copy
 *  - DemoQuestion:     a real, answerable SAT question that grades itself
 *                      instantly — the product's magic moment, performed
 *                      before the visitor scrolls
 *  - CountUp:          animated stat counters
 */

import { useEffect, useState } from "react";
import Link from "next/link";

const WORDS = ["TOEFL", "SAT", "GRE", "IELTS", "NCLEX", "ACT", "GMAT"];

export function RotatingExamWord() {
  const [i, setI] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => { setI(x => (x + 1) % WORDS.length); setVisible(true); }, 180);
    }, 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <span
      className="text-emerald-700 font-semibold border-b-2 border-emerald-400 transition-opacity duration-200"
      style={{ opacity: visible ? 1 : 0 }}
    >
      {WORDS[i]}
    </span>
  );
}

export function CountUp({ target, suffix = "", statKey }: { target: number; suffix?: string; statKey?: "exams_scored" | "questions_graded" }) {
  // Always render a real number — the static fallback server-side and on the
  // first client paint, then the live figure from /api/stats when it arrives.
  // A previous version counted up from 0 on scroll-into-view, but any counter
  // that wasn't already on screen (short viewports, below the fold) sat at a
  // bare "0+" until scrolled to. Showing the number outright removes that in
  // every viewport; the fallback→live update keeps it feeling alive.
  const [v, setV] = useState(target);

  useEffect(() => {
    if (!statKey) return;
    fetch("/api/stats")
      .then(r => r.json())
      .then(d => { if (typeof d?.[statKey] === "number" && d[statKey] > 0) setV(d[statKey]); })
      .catch(() => {});
  }, [statKey]);

  return <span>{v.toLocaleString()}{suffix}</span>;
}

// Small rotating pool — shows exam breadth and gives repeat visitors a
// fresh question. Every stem/key hand-verified.
const DEMO_QUESTIONS = [
  {
    tag: "SAT",
    stem: "If 3x − 7 = 14, what is the value of 6x + 1?",
    options: [
      { k: "A", text: "22" },
      { k: "B", text: "42" },
      { k: "C", text: "43" },
      { k: "D", text: "49" },
    ],
    correct: "C",
    right: "Correct. 3x = 21, so x = 7, and 6x + 1 = 43. Notice 6x + 1 = 2(3x − 7) + 15 — the fast path.",
    wrong: "Not quite — 3x = 21, so x = 7, and 6x + 1 = 43. This is exactly the feedback you get on every question.",
  },
  {
    tag: "TOEFL",
    stem: "The findings were so compelling that even skeptical reviewers conceded the theory had merit. \"Conceded\" most nearly means:",
    options: [
      { k: "A", text: "denied" },
      { k: "B", text: "admitted" },
      { k: "C", text: "calculated" },
      { k: "D", text: "repeated" },
    ],
    correct: "B",
    right: "Correct. To concede is to admit something reluctantly — the skeptics didn't want the theory to have merit, but accepted it.",
    wrong: "To concede is to admit reluctantly — skeptics accepting a point against their position. The answer is B.",
  },
  {
    tag: "GRE",
    stem: "A store cuts a price by 20%, then raises the new price by 20%. The final price is what percent of the original?",
    options: [
      { k: "A", text: "96%" },
      { k: "B", text: "98%" },
      { k: "C", text: "100%" },
      { k: "D", text: "104%" },
    ],
    correct: "A",
    right: "Correct. 0.8 × 1.2 = 0.96 — successive percent changes multiply, they don't cancel. A classic GRE trap avoided.",
    wrong: "Percent changes multiply: 0.8 × 1.2 = 0.96, so 96%. The \"they cancel out\" instinct is the trap — answer A.",
  },
];

export function DemoQuestion() {
  const [qIdx, setQIdx] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  // Randomize after mount (not in the initializer) so SSR and first client
  // render agree — avoids a hydration mismatch.
  useEffect(() => { setQIdx(Math.floor(Math.random() * DEMO_QUESTIONS.length)); }, []);
  const q = DEMO_QUESTIONS[qIdx];
  const answered = picked !== null;
  const correct = picked === q.correct;

  return (
    <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-cream-lg border border-cream relative">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11px] text-[var(--text-muted)] font-medium">{q.tag} · try one right now</span>
        <span
          className={`text-[10px] font-semibold rounded-full px-2.5 py-1 transition-colors ${
            answered
              ? "text-emerald-700 bg-emerald-50 border border-emerald-200"
              : "text-[var(--text-muted)] border border-dashed border-[var(--border)]"
          }`}
        >
          {answered ? "graded in 0.3s" : "ungraded"}
        </span>
      </div>

      <div className="text-[15px] font-medium text-[var(--text-primary)] leading-relaxed mb-4">
        {q.stem}
      </div>

      <div className="space-y-2">
        {q.options.map(o => {
          const isCorrect = o.k === q.correct;
          const isPick = picked === o.k;
          let cls = "border-[var(--border)] text-[var(--text-secondary)] hover:border-emerald-400";
          if (answered) {
            if (isCorrect) cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
            else if (isPick) cls = "border-red-300 bg-red-50 text-red-700";
            else cls = "border-[var(--border)] text-[var(--text-muted)] opacity-50";
          }
          return (
            <button
              key={o.k}
              onClick={() => !answered && setPicked(o.k)}
              disabled={answered}
              className={`w-full text-left border rounded-xl px-4 py-2.5 text-[13px] font-medium transition-all ${cls} ${answered ? "cursor-default" : "cursor-pointer"}`}
            >
              {o.k}. {o.text}
              {answered && isCorrect && <span className="ml-2 text-emerald-600 font-bold">✓</span>}
            </button>
          );
        })}
      </div>

      {answered && (
        <div
          className={`mt-4 text-[12px] leading-relaxed rounded-xl px-4 py-3 ${
            correct ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-800"
          }`}
          style={{ animation: "fptPopIn 0.28s ease-out" }}
        >
          {correct ? q.right : q.wrong}
        </div>
      )}

      {answered && (
        <>
          <Link
            href="/auth?mode=signup"
            className="mt-3 block text-center text-[13px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl py-2.5 transition-colors"
            style={{ animation: "fptPopIn 0.28s ease-out 0.1s backwards" }}
          >
            That took 0.3s — now imagine the full exam →
          </Link>
          <button
            onClick={() => { setPicked(null); setQIdx(i => (i + 1) % DEMO_QUESTIONS.length); }}
            className="mt-2 w-full text-center text-[12px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            Try another question ↻
          </button>
        </>
      )}
    </div>
  );
}
